import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { collabStateManager } from '@databases/collab-state-manager';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import { getVersionId, useDatabaseContent } from '@databases/database-state';
import { isAIFCStagingAreaView } from '@databases/database-state';
import { setDatabaseContentReady } from '@databases/database-state/state/initial-database-loading-state';
import { isAnyNativeEmbed, isStandalone as isStandaloneCheck } from '@databases/modes';
import { appStoreUtils } from '@databases/redux/store';
import { AsyncOperationStatus } from '@databases/redux-types';
import { storage as createWithTemplateStorage } from '@databases/templates/storage';
import { useApplySystemTemplate } from '@databases/templates/useApplySystemTemplate';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { selectDatabaseByContentIdStatus } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import React, { type FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { getFrameApi } from '@canvas/frame-api/host-api/host-frame-context';

import { DatabaseError } from './DatabaseError';
import { type DatabaseActionKey } from './common/components/database/DatabaseActions';
import { DatabaseBlankObjectExperienceModal } from './common/components/database-blank-object-experience-modal/DatabaseBlankObjectExperienceModal';
import { DatabaseBoeThinkingBar } from './common/components/database-blank-object-experience-modal/DatabaseBoeThinkingBar';
import DatabaseContent from './common/components/database-content/DatabaseContent';
import DatabaseContentSkeleton from './common/components/database-content/DatabaseContentSkeleton';
import { DatabaseInitialLoadingState } from './common/components/database-skeletons/DatabaseInitialLoadingState';

const isStandalone = isStandaloneCheck();

const actions: DatabaseActionKey[] = ['import', 'print', 'help', 'copy', 'feedback'];
if (isStandalone) {
  actions.push('lock');
}

interface DatabaseContentLoaderProps {
  onDataLoaded?: () => void;
}

const DatabaseContentLoader: FunctionComponent<DatabaseContentLoaderProps> = ({
  onDataLoaded,
}) => {
  const { contentId } = useDatabaseContent();
  const versionId = getVersionId();
  const isDbAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const { firstLayoutProcessed } = useSnapshot(aiFirstCreationStreamingState);
  const { isStreaming } = useDatabaseStreamState();
  const isInAIFCStagingAreaView = isAIFCStagingAreaView();
  const { view } = useView();

  const frameApiHref = getFrameApi().href;
  const frameApiHrefUrl = new URL(frameApiHref);
  const hasAiCreateFlowQueryParam =
    frameApiHrefUrl.searchParams.get('flow') === 'aiCreate';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isThinkingBarVisible, setIsThinkingBarVisible] = useState(false);
  const [hasHandledCreation, setHasHandledCreation] = useState(false);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleBoeSubmit = useCallback(() => {
    setIsModalOpen(false);
    setIsThinkingBarVisible(true);
  }, []);

  const handleThinkingBarDismiss = useCallback(() => {
    setIsThinkingBarVisible(false);
  }, []);

  useApplySystemTemplate();

  const databaseStatus = appStoreUtils.useEqualSelector((state) =>
    selectDatabaseByContentIdStatus(state, { contentId })
  );

  useEffect(() => {
    const logMetrics = async () => {
      GlobalPageLoadExperience.mark('database-load-end');
      GlobalPageLoadExperience.success({ force: true });
      (await getBridgeAPI()).onContentLoadSucceeded?.({});
    };

    if (databaseStatus?.status === AsyncOperationStatus.SUCCEEDED) {
      logMetrics();
      onDataLoaded?.();
    }
  }, [databaseStatus?.status, contentId, onDataLoaded]);

  const isDatabaseLoaded = databaseStatus?.status === AsyncOperationStatus.SUCCEEDED;

  useEffect(() => {
    if (
      !isDatabaseLoaded ||
      !getExperimentValue('cwrBlankObjectExperiment', 'isEnabled', {
        fireExperimentExposure: false,
      })
    ) {
      return;
    }

    const collabManager = collabStateManager.getCollabManager(contentId);
    if (!hasHandledCreation && collabManager?.wasCreatedThisSession) {
      setHasHandledCreation(true);
      if (
        isDbAiFirstCreationEnabled &&
        !hasAiCreateFlowQueryParam &&
        !isInAIFCStagingAreaView &&
        !isAnyNativeEmbed() &&
        !createWithTemplateStorage.exists(contentId) &&
        ['variant1', 'variant2'].includes(
          getExperimentValue('cwrBlankObjectExperiment', 'cohort', {
            fireExperimentExposure: true,
          })
        )
      ) {
        setIsModalOpen(true);
      }
    }
    // Runs when database loads AND when experiment flag resolves (async).
    // hasHandledCreation ensures we only show modal/panel once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDatabaseLoaded]);

  const isAIStreamingWithoutLayout =
    isDbAiFirstCreationEnabled &&
    isStreaming &&
    isInAIFCStagingAreaView &&
    // firstLayoutProcessed is always false in standalone, so we should only
    // check for databases in AIFC staging area
    !firstLayoutProcessed;
  const shouldShowSkeleton = !isDatabaseLoaded || isAIStreamingWithoutLayout;
  useEffect(() => {
    if (!shouldShowSkeleton) {
      setDatabaseContentReady();
    }
  }, [shouldShowSkeleton]);

  if (databaseStatus?.status === AsyncOperationStatus.ERROR) {
    return <DatabaseError serializedError={databaseStatus.error} />;
  }

  if (shouldShowSkeleton) {
    return view.id && !isAIStreamingWithoutLayout ? (
      <DatabaseContentSkeleton layoutType={view.settings.layout.type} />
    ) : (
      <DatabaseInitialLoadingState />
    );
  }

  return (
    <>
      {isModalOpen && (
        <DatabaseBlankObjectExperienceModal
          onClose={handleModalClose}
          onSubmit={handleBoeSubmit}
        />
      )}
      {isThinkingBarVisible && (
        <DatabaseBoeThinkingBar
          contentId={contentId}
          onDismiss={handleThinkingBarDismiss}
        />
      )}
      <DatabaseContent
        contentId={contentId}
        isContentEditable={!versionId}
        actions={actions}
      />
    </>
  );
};

export default DatabaseContentLoader;
